import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Database, Cloud, X, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

const LoginPromptModal = ({ 
  isOpen, 
  onClose, 
  expenseCount = 0, 
  onLogin,
  variant = 'subtle' // 'subtle' ou 'strong'
}) => {
  if (!isOpen) return null;

  const isStrong = variant === 'strong';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="relative w-full max-w-md bg-[#0A0A0A] border border-white/10 rounded-2xl p-6 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button - só mostra se for variant sutil */}
          {!isStrong && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}

          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
              isStrong 
                ? 'bg-gradient-to-br from-amber-500/20 to-orange-500/20' 
                : 'bg-[#00FF94]/10'
            }`}>
              {isStrong ? (
                <Database className="w-8 h-8 text-amber-400" />
              ) : (
                <Cloud className="w-8 h-8 text-[#00FF94]" />
              )}
            </div>
          </div>

          {/* Title */}
          <h2 className="text-xl font-bold text-white text-center mb-2">
            {isStrong 
              ? `Não perca suas ${expenseCount} despesas!` 
              : 'Seus dados estão só neste dispositivo'}
          </h2>

          {/* Description */}
          <p className="text-gray-400 text-center mb-6">
            {isStrong 
              ? 'Você investiu tempo organizando suas finanças. Crie uma conta grátis para não perder esse progresso!'
              : 'Crie uma conta grátis para acessar suas despesas de qualquer lugar e nunca perder seus dados.'}
          </p>

          {/* Benefits */}
          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-3 text-sm text-gray-300">
              <div className="w-6 h-6 rounded-full bg-[#00FF94]/10 flex items-center justify-center flex-shrink-0">
                <Cloud className="w-3.5 h-3.5 text-[#00FF94]" />
              </div>
              <span>Acesse de qualquer dispositivo</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-300">
              <div className="w-6 h-6 rounded-full bg-[#00FF94]/10 flex items-center justify-center flex-shrink-0">
                <Shield className="w-3.5 h-3.5 text-[#00FF94]" />
              </div>
              <span>Seus dados protegidos na nuvem</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-300">
              <div className="w-6 h-6 rounded-full bg-[#00FF94]/10 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-3.5 h-3.5 text-[#00FF94]" />
              </div>
              <span>Continue de onde parou</span>
            </div>
          </div>

          {/* Buttons */}
          <div className="space-y-3">
            <Button
              className="w-full bg-[#00FF94] text-black hover:bg-[#00CC76] font-bold py-6"
              onClick={onLogin}
              data-testid="login-prompt-btn"
            >
              <Shield className="w-5 h-5 mr-2" />
              Criar Conta Grátis
            </Button>
            
            {!isStrong && (
              <Button
                variant="ghost"
                className="w-full text-gray-400 hover:text-white"
                onClick={onClose}
              >
                Continuar sem conta
              </Button>
            )}
            
            {isStrong && (
              <button
                onClick={onClose}
                className="w-full text-sm text-gray-500 hover:text-gray-400 py-2"
              >
                Lembrar depois
              </button>
            )}
          </div>

          {/* Footer note */}
          <p className="text-xs text-gray-600 text-center mt-4">
            Login rápido com Google • Sem cartão de crédito
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LoginPromptModal;
